import React from 'react';
import { Lightbulb } from 'lucide-react';
import { formatCurrencyPrecise } from '../../../utils/formatting';

interface PrimeCalculationExampleProps {
  title: string;
  steps: Array<{
    label: string;
    value: string | number;
    calculation?: string;
  }>;
  result: {
    label: string;
    value: string | number;
  };
  color?: 'blue' | 'green' | 'yellow' | 'purple' | 'red' | 'indigo' | 'teal' | 'cyan' | 'emerald';
}

const colorClasses = {
  blue: 'bg-gray-50 border-gray-200 text-gray-800 text-gray-700',
  green: 'bg-gray-50 border-gray-200 text-gray-800 text-gray-700',
  yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800 text-yellow-700',
  purple: 'bg-gray-50 border-gray-200 text-gray-800 text-gray-700',
  red: 'bg-gray-50 border-gray-200 text-gray-800 text-gray-700',
  indigo: 'bg-gray-50 border-gray-200 text-gray-800 text-gray-700',
  teal: 'bg-gray-50 border-gray-200 text-gray-800 text-gray-700',
  cyan: 'bg-gray-50 border-gray-200 text-gray-800 text-gray-700',
  emerald: 'bg-gray-50 border-gray-200 text-gray-800 text-gray-700',
  brown: 'bg-gray-50 border-gray-200 text-gray-800 text-gray-700'
};

export default function PrimeCalculationExample({ 
  title, 
  steps, 
  result, 
  color = 'blue' 
}: PrimeCalculationExampleProps) {
  const classes = colorClasses[color];
  const [bgClass, borderClass, titleClass, textClass] = classes.split(' ');

  return (
    <div className={`p-3 ${bgClass} rounded-lg border ${borderClass}`}>
      <h4 className={`text-sm font-medium ${titleClass} mb-2 flex items-center gap-1`}>
        <Lightbulb className="w-4 h-4" />
        {title}
      </h4>
      <div className={`text-xs ${textClass} space-y-1`}>
        {steps.map((step, index) => (
          <div key={index} className="flex justify-between items-center">
            <span>
              {step.label}
              {step.calculation && (
                <span className="text-xs opacity-75 ml-1">({step.calculation})</span>
              )}
            </span>
            <span className="font-medium">
              {typeof step.value === 'number' ? formatCurrencyPrecise(step.value) : step.value}
            </span>
          </div>
        ))}
        <div className="flex justify-between items-center font-medium border-t border-gray-300 pt-1 mt-2">
          <span>{result.label}</span>
          <span>
            {typeof result.value === 'number' ? formatCurrencyPrecise(result.value) : result.value}
          </span>
        </div>
      </div>
    </div>
  );
}